import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGeolocation, submitLead } from '@/lib/api';
import { countries, findCountryByCode } from '@/data/countries';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LeadForm({ packageTitle = 'Essentials', packageAmount = 45, tmSignUpType = 1, onSuccess, embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [form, setForm] = useState({ name: '', email: '', phoneNumber: '', message: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getGeolocation().then((data) => {
      if (data?.isSuccess && data?.data?.countryCode) {
        setGeoData(data);
        const found = findCountryByCode(data.data.countryCode);
        if (found) setSelectedCountry(found);
      }
    });
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.phoneNumber.trim()) errs.phoneNumber = 'Phone number is required';
    else if (!/^\d+$/.test(form.phoneNumber)) errs.phoneNumber = 'Only numbers allowed';
    else if (form.phoneNumber.length > 10) errs.phoneNumber = 'Maximum 10 digits';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const numericOnly = value.replace(/\D/g, '').slice(0, 10);
      setForm(prev => ({ ...prev, [name]: numericOnly }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await submitLead({
        name: form.name,
        email: form.email,
        countryDialCode: selectedCountry.dial,
        countryCode: selectedCountry.code,
        phoneNumber: form.phoneNumber,
        message: 'N/A',
        packageTitle,
        packageAmount,
        tmSignUpType,
      });

      if (result.status === 200 && result.data?.data?.customerId) {
        toast.success('Submitted successfully!');
        if (onSuccess) onSuccess();
        window.location.href = `/application/main?customerid=${result.data.data.customerId}`;
      } else if (result.status === 401) {
        toast.error(result.data?.message || 'Unauthorized request');
      } else if (result.status === 400) {
        toast.error(result.data?.errorMessage || 'Invalid submission');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "h-11 bg-white border-slate-200 focus:border-[#10B981] focus:ring-[#10B981]";

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      <div>
        <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
        <Input
          id="name"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="John Doe"
          className={inputClass}
          data-testid="lead-form-name"
        />
        {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="error-name">{errors.name}</p>}
      </div>

      <div>
        <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="john@example.com"
          className={inputClass}
          data-testid="lead-form-email"
        />
        {errors.email && <p className="text-xs text-red-500 mt-1" data-testid="error-email">{errors.email}</p>}
      </div>

      <div>
        <Label className="text-sm font-medium text-slate-700">Phone Number</Label>
        <div className="flex gap-2">
          <select
            value={selectedCountry.code}
            onChange={(e) => {
              const c = countries.find(co => co.code === e.target.value);
              if (c) setSelectedCountry(c);
            }}
            className="h-11 w-[130px] rounded-md border border-slate-200 bg-white px-2 text-sm focus:border-[#10B981] focus:ring-[#10B981] focus:outline-none"
            data-testid="lead-form-country-code"
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                +{c.dial} {c.code}
              </option>
            ))}
          </select>
          <Input
            name="phoneNumber"
            value={form.phoneNumber}
            onChange={handleChange}
            placeholder="1234567890"
            className={`${inputClass} flex-1`}
            maxLength={10}
            data-testid="lead-form-phone"
          />
        </div>
        {errors.phoneNumber && <p className="text-xs text-red-500 mt-1" data-testid="error-phone">{errors.phoneNumber}</p>}
      </div>

      <div className="hidden">
        <Label htmlFor="message" className="text-sm font-medium text-slate-700">Message</Label>
        <Textarea
          id="message"
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us about your trademark needs..."
          className="bg-white border-slate-200 focus:border-[#10B981] focus:ring-[#10B981] min-h-[80px]"
          data-testid="lead-form-message"
        />
        {errors.message && <p className="text-xs text-red-500 mt-1" data-testid="error-message">{errors.message}</p>}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full h-11 bg-[#064E3B] text-white rounded-full font-medium text-sm hover:bg-[#065F46] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="lead-form-submit"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Get Started Now'}
      </button>
    </form>
  );
}
